import classnames from 'classnames'
import styles from './Cursor.module.scss'
import { useEffect, useMemo, useRef, useState } from 'react'
import gsap from 'gsap'
import useStore from '@/store'
import useBreakpoint from '@/hooks/use-breakpoint'
import Stick from '@/components/_svg/Stick'
import useWindowResize from '@/hooks/use-window-resize'

const TEXT_ANIMATION_DURATION = 0.4

const Cursor = ({ className }) => {
  const containerRef = useRef()
  const horizontalLineRef = useRef()
  const verticalLineRef = useRef()
  const cursorState = useStore(state => state.cursorState)
  const setCursorPosition = useStore(state => state.setCursorPosition)
  const { isMobile } = useBreakpoint()
  const showCursorRef = useRef(false)
  const $textContainer = useRef(null)
  const [texts, setTexts] = useState([])
  const debounceResetItemsTimeout = useRef(null)
  const loaderAnimationComplete = useStore(state => state.loaderAnimationComplete)
  const theme = useStore(state => state.theme)
  const zoomImageOpen = useStore(state => state.zoomImageOpen)
  const resizeKey = useWindowResize()
  const isCustomText = useMemo(() => {
    if (cursorState && cursorState !== 'FOCUS' && cursorState !== null) {
      return true
    }
    return false
  }, [cursorState])

  useEffect(() => {
    const newText = isCustomText ? cursorState : ''
    setTexts(prev => {
      if (prev[prev?.length - 1] === newText) {
        return prev
      }

      return [...prev, newText]
    })
  }, [isCustomText, cursorState])

  useEffect(() => {
    if (!$textContainer.current) return

    setTimeout(() => {
      if (isMobile || isMobile === null) return
      const items = $textContainer.current.querySelectorAll('[data-cursor-text]')
      gsap.killTweensOf(items)
      gsap.to(items, {
        y: i => {
          if (i === items.length - 1) return 0
          return '-105%'
        },
        duration: TEXT_ANIMATION_DURATION,
        ease: 'Power4.easeOut',
      })
    }, 5)
  }, [cursorState, isMobile])

  useEffect(() => {
    if (debounceResetItemsTimeout.current) {
      clearTimeout(debounceResetItemsTimeout.current)
    }

    if (cursorState !== null && cursorState !== 'FOCUS') {
      return
    }

    debounceResetItemsTimeout.current = setTimeout(
      () => {
        setTexts([])
      },
      TEXT_ANIMATION_DURATION * 2 * 1000,
    )
  }, [cursorState])

  useEffect(() => {
    gsap.set(containerRef.current, { xPercent: -50, yPercent: -50 })

    const duration = 0.6
    const xTo = gsap.quickTo(containerRef.current, 'x', { duration, ease: 'power3' })
    const yTo = gsap.quickTo(containerRef.current, 'y', { duration, ease: 'power3' })

    const move = (e, hasMovedCursor = true) => {
      xTo(e.clientX)
      yTo(e.clientY)

      setCursorPosition({
        x: e.clientX,
        y: e.clientY,
      })

      if (hasMovedCursor && !showCursorRef.current) {
        showCursorRef.current = true
        gsap.to(containerRef.current, {
          autoAlpha: 1,
          duration: 0.8,
          delay: duration * 0.5,
        })
      }
    }

    setTimeout(() => {
      move(
        {
          clientX: window.innerWidth * 0.5,
          clientY: window.innerHeight * 0.5,
        },
        false,
      )
    }, 100)

    window.removeEventListener('mousemove', move)

    if (!isMobile) {
      window.addEventListener('mousemove', move)
    }
  }, [isMobile, setCursorPosition, resizeKey])

  useEffect(() => {
    if (isMobile) return

    gsap.killTweensOf([horizontalLineRef.current, verticalLineRef.current])

    const duration = 0.2
    const ease = 'Power3.easeOut'

    gsap.killTweensOf([horizontalLineRef.current, verticalLineRef.current])

    const animateToDefault = () => {
      gsap.to(horizontalLineRef.current, {
        scale: 1,
        duration,
        ease,
      })
      gsap.to(verticalLineRef.current, {
        scale: 1,
        duration,
        ease,
      })
    }

    const animateToFocus = () => {
      gsap.to(horizontalLineRef.current, {
        scale: 1.4,
        duration,
        ease,
      })
      gsap.to(verticalLineRef.current, {
        scale: 1.4,
        duration,
        ease,
      })
    }

    switch (cursorState) {
      case 'FOCUS':
        animateToFocus()
        break
      case null:
        animateToDefault()
        break
      default:
        animateToFocus()
        break
    }
  }, [cursorState, isMobile])

  if (isMobile) return null

  let calculatedTheme = theme
  if (!loaderAnimationComplete || zoomImageOpen) {
    calculatedTheme = theme === 'dark' ? 'light' : 'dark'
  }

  return (
    <div
      className={classnames(styles.Cursor, className)}
      data-theme={calculatedTheme}
      ref={containerRef}
    >
      <div className={styles.crosshair}>
        <div
          className={styles.crosshair__vertical}
          ref={verticalLineRef}
        >
          <Stick className={styles.crosshair__stick} />
        </div>
        <div
          className={styles.crosshair__horizontal}
          ref={horizontalLineRef}
        >
          <Stick className={styles.crosshair__stick} />
        </div>
      </div>
      <div
        className={styles.customTextContainer}
        ref={$textContainer}
      >
        {texts.map((text, index) => (
          <span
            key={`${index}_${text}`}
            data-cursor-text={text}
            className={styles.customText}
          >
            {text}
          </span>
        ))}
      </div>
    </div>
  )
}

Cursor.displayName = 'Cursor'

export default Cursor
